import { getServerUrl } from '../utils';

export const BASE_URL = getServerUrl();

export const TEST_CONNECTION_STATUS = {
  OK: 'ok',
  ERROR: 'error',
} as const;

export const SYNC_STATUSES = {
  SUCCESS: 'success',
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  FAILED: 'failed',
  SUSPENDED: 'suspended',
} as const;

/**
 * Classification of a failed sync, mirrored from the backend `error_type` field.
 * Field names on the API are camelCase, but the values are snake_case strings.
 */
export const SYNC_ERROR_TYPES = {
  AUTHENTICATION: 'authentication',
  TRANSIENT: 'transient',
  PERMANENT: 'permanent',
  INVALID_INSTALLATION: 'invalid_installation',
  INTERNAL: 'internal',
} as const;

export const REPO_STATUSES = {
  PENDING: 'pending',
  SUCCESS: 'success',
  SUSPENDED: 'suspended',
} as const;

/**
 * Health of a repository's webhook, mirrored from the backend `webhook_health`
 * field. Null/undefined for non-GitLab repos or repos with no webhook.
 *
 * @deprecated Prefer `CONNECTION_HEALTH` — the normalized, provider-agnostic
 * signal. Kept for backward compatibility during the backend rollout.
 */
export const WEBHOOK_HEALTH = {
  HEALTHY: 'healthy',
  TEMPORARILY_DISABLED: 'temporarily_disabled',
  DISABLED: 'disabled',
} as const;

/**
 * Normalized, provider-agnostic connection health, mirrored from the backend
 * `connection_health` field. Present for every provider: GitLab maps its
 * precise per-hook signal onto it; others derive a baseline from sync state.
 * `pending` is onboarding / reinstall actively verifying; `unknown` means "no
 * signal yet" and renders as a muted badge.
 */
export const CONNECTION_HEALTH = {
  HEALTHY: 'healthy',
  DEGRADED: 'degraded',
  DISABLED: 'disabled',
  PENDING: 'pending',
  UNKNOWN: 'unknown',
} as const;

export const TRACKING_MODE = {
  PER_PROJECT: 'per_project',
  GROUP_WEBHOOK: 'group_webhook',
} as const;

/**
 * PR Knowledge indexing status, mirrored 1:1 from the backend
 * `prKnowledge.status` field (see `PrKnowledgeStatus` in qodo-platform's
 * `pr_knowledge_schemas.py`). User-facing labels in parentheses:
 * - `not_started` — the engine has no task row for the repo yet.
 * - `queued` ("Queued") — a first-time run is queued, not yet picked up.
 * - `in_progress` ("Indexing") — the initial history walk is running.
 * - `completed` ("Active") — the initial walk finished; context is served.
 * - `active_queued` ("Active, Queued") — active, with a delta run queued.
 * - `updating` ("Active, Updating") — a delta run is indexing new merges on an
 *   already-active repo; existing context stays available.
 * - `active_attention` ("Active*") — active, but enough recent delta jobs
 *   failed to warrant attention; existing context stays available.
 * - `failed` ("Failed") — the initial index exhausted retries / went silent,
 *   so nothing usable was indexed. Distinct from `not_started`.
 */
export const PR_KNOWLEDGE_STATUSES = {
  NOT_STARTED: 'not_started',
  QUEUED: 'queued',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  ACTIVE_QUEUED: 'active_queued',
  UPDATING: 'updating',
  ACTIVE_ATTENTION: 'active_attention',
  FAILED: 'failed',
} as const;

/**
 * Operations the repository-operations endpoint serves. `index` covers both the
 * first-time index and a delta update — the backend picks which from the repo's
 * current state.
 */
export const REPOSITORY_OPERATIONS = {
  INDEX: 'index',
} as const;

/**
 * Statuses where the initial history walk has finished, so PR-knowledge data
 * is available and its dependent features unlock — mirrors the backend's
 * completed-equivalent set (`completed` | `active_attention` | `updating` |
 * `active_queued`).
 */
export const PR_KNOWLEDGE_COMPLETED_EQUIVALENT_STATUSES = [
  PR_KNOWLEDGE_STATUSES.COMPLETED,
  PR_KNOWLEDGE_STATUSES.ACTIVE_ATTENTION,
  PR_KNOWLEDGE_STATUSES.UPDATING,
  PR_KNOWLEDGE_STATUSES.ACTIVE_QUEUED,
] as const;
